import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { USER_ROLES, STORAGE_KEYS } from '../utils/videocall/constants'

const RoomContext = createContext(null)

export function RoomProvider({ children }) {
  const [roomId, setRoomId] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [participants, setParticipants] = useState([])
  const [isConnected, setIsConnected] = useState(false)

  /**
   * Initialize room
   */
  const initRoom = useCallback((id, role = null) => {
    setRoomId(id)

    // Determine role if not provided
    let finalRole = role
    if (!finalRole) {
      const storedRole = sessionStorage.getItem(`${STORAGE_KEYS.ROOM_CREATOR}${id}`)
      finalRole = storedRole === 'true' ? USER_ROLES.INTERVIEWER : USER_ROLES.INTERVIEWEE
    }

    setUserRole(finalRole)

    // Store role
    if (finalRole === USER_ROLES.INTERVIEWER) {
      sessionStorage.setItem(`${STORAGE_KEYS.ROOM_CREATOR}${id}`, 'true')
    }
  }, [])

  /**
   * Set room as creator
   */
  const setAsCreator = useCallback((id) => {
    sessionStorage.setItem(`${STORAGE_KEYS.ROOM_CREATOR}${id}`, 'true')
    setUserRole(USER_ROLES.INTERVIEWER)
  }, [])

  /**
   * Check if user is interviewer
   */
  const isInterviewer = useCallback(() => {
    return userRole === USER_ROLES.INTERVIEWER
  }, [userRole])

  /**
   * Add participant
   */
  const addParticipant = useCallback((participantId) => {
    setParticipants(prev => {
      if (prev.includes(participantId)) return prev
      return [...prev, participantId]
    })
  }, [])

  /**
   * Remove participant
   */
  const removeParticipant = useCallback((participantId) => {
    setParticipants(prev => prev.filter(id => id !== participantId))
  }, [])

  /**
   * Clear room
   */
  const clearRoom = useCallback(() => {
    setRoomId(null)
    setUserRole(null)
    setParticipants([])
    setIsConnected(false)
  }, [])

  const value = useMemo(() => ({
    roomId,
    userRole,
    participants,
    isConnected,
    setIsConnected,
    initRoom,
    setAsCreator,
    isInterviewer,
    addParticipant,
    removeParticipant,
    clearRoom,
  }), [
    roomId,
    userRole,
    participants,
    isConnected,
    setIsConnected,
    initRoom,
    setAsCreator,
    isInterviewer,
    addParticipant,
    removeParticipant,
    clearRoom,
  ])

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>
}

export function useRoom() {
  const context = useContext(RoomContext)
  if (!context) {
    throw new Error('useRoom must be used within RoomProvider')
  }
  return context
}
